'use client';

import BodyLink from '@jouzen/ecom-components/v3/BodyLink';
import { useTranslations } from 'next-intl';

import { OrderInfo, SectionHeading } from '@/app/components/ui';
import { useAccountData, useOrderData } from '@/app/hooks';
import useEligibilityData from '@/app/hooks/useEligibilityData';
import { box } from '@/app/styles/ui';
import checkEligibilityFlow from '@/app/utils/checkEligibilityFlow';
import { usePathname, useRouter } from '@/i18n/routing';
import { OrderStatus, PaymentMethod } from '@/types/Order';

import { STEP } from '../const';

const BusinessReviewOrder = () => {
  const t = useTranslations();
  const { push } = useRouter();
  const currentStep = usePathname();
  const {
    shippingAddress: orderShippingAddress,
    orderStatus,
    paymentMethod,
  } = useOrderData();
  const eligibility = useEligibilityData();
  const isEligibilityFlow = checkEligibilityFlow(eligibility);
  const { email } = useAccountData();

  const shippingAddress =
    isEligibilityFlow && eligibility.shippingAddress
      ? eligibility.shippingAddress
      : orderShippingAddress;

  // Always show the Edit Address link on /select and /review-enrollment in
  // the eligibility flow. In the order flow, only show it when there's an
  // editable address — i.e. a pending net-terms order with a populated
  // address.
  const isAddressEditableStep =
    currentStep === STEP.SELECT.path ||
    currentStep === STEP.REVIEW_ENROLLMENT.path;
  const canEditOrderAddress =
    orderStatus === OrderStatus.pending &&
    paymentMethod === PaymentMethod.net_terms &&
    Boolean(shippingAddress?.address);
  const showEditAddress =
    isAddressEditableStep && (isEligibilityFlow || canEditOrderAddress);

  return (
    <section className="flex flex-col gap-2">
      <section className="flex items-center justify-between">
        <SectionHeading>
          {t('eop_order_selections_page_review_order')}
        </SectionHeading>

        {showEditAddress && (
          <p className="text-blue-100">
            <BodyLink
              color="inherit"
              onClick={(e) => {
                e.preventDefault();
                // Send the user back to the step they came from after
                // editing the address.
                const returnTo =
                  currentStep === STEP.REVIEW_ENROLLMENT.path
                    ? 'review-enrollment'
                    : 'select';
                push(`${STEP.DELIVERY.path}?returnTo=${returnTo}`);
              }}
            >
              {t('eop_order_selections_page_edit_address')}
            </BodyLink>
          </p>
        )}
      </section>

      <OrderInfo className={box()} email={email} address={shippingAddress} />
    </section>
  );
};

export default BusinessReviewOrder;
